import { z } from "zod";
import type { ErrorCode } from "@hybrid-escrow/shared";
import {
  adminLoginRequestSchema,
  adminSessionResponseSchema,
  adminUserSummarySchema,
  adminUserDetailSchema,
  adminSetUserStatusRequestSchema,
  adminMetricsSchema,
  adminFlaggedMessageSchema,
  adminAuditEventSchema,
  maintenanceModeResponseSchema,
  setMaintenanceModeRequestSchema,
  listingResponseSchema,
  disputeResponseSchema,
  paginatedResponseSchema,
  ticketResponseSchema,
  ticketDetailResponseSchema,
  createTicketMessageRequestSchema,
  adminSetTicketStatusRequestSchema,
  adminSetTicketPriorityRequestSchema,
  couponResponseSchema,
  createCouponRequestSchema,
  accountTransferQueueItemSchema,
  submitAccountTransferCredentialsRequestSchema,
  type SubmitAccountTransferCredentialsRequest,
  accountTransferSellerHandoffResponseSchema,
  domainTransferQueueItemSchema,
  domainTransferCredentialResponseSchema,
  postResponseSchema,
  postSummaryResponseSchema,
  createPostRequestSchema,
  updatePostRequestSchema,
  type CreatePostRequest,
  type UpdatePostRequest,
  type AdminSessionResponse,
  type AdminUserDetail,
  type AdminMetrics,
  type MaintenanceModeResponse,
  type SetMaintenanceModeRequest,
  type TicketDetailResponse,
  type CreateTicketMessageRequest,
  type AdminSetTicketStatusRequest,
  type AdminSetTicketPriorityRequest,
  type CreateCouponRequest,
} from "@hybrid-escrow/shared";
import { apiBaseUrl } from "./env";
import { ApiClientError, ApiNetworkError } from "./api-client";
import { getAdminToken } from "./adminAuth";

interface AdminRequestOptions {
  method?: "GET" | "POST" | "PATCH";
  body?: unknown;
}

async function adminRequest<T>(
  path: string,
  schema: z.ZodType<T, z.ZodTypeDef, unknown>,
  options: AdminRequestOptions = {},
): Promise<T> {
  const { method = "GET", body } = options;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const token = getAdminToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    throw new ApiNetworkError(err);
  }

  const isJson = response.headers.get("content-type")?.includes("application/json") ?? false;
  const json: unknown = isJson ? await response.json().catch(() => undefined) : undefined;

  if (!response.ok) {
    const apiErrorBodySchema = z.object({
      error: z.object({
        code: z.string(),
        message: z.string(),
        requestId: z.string(),
        details: z.unknown().optional(),
      }),
    });
    const parsedError = apiErrorBodySchema.safeParse(json);
    if (parsedError.success) {
      const { code, message, requestId, details } = parsedError.data.error;
      throw new ApiClientError(code as ErrorCode, response.status, message, requestId, details);
    }
    throw new ApiClientError(
      "UNKNOWN_ERROR",
      response.status,
      `Request failed with status ${response.status}`,
    );
  }

  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new Error(
      `Admin API response for ${method} ${path} did not match the expected schema: ${parsed.error.message}`,
    );
  }
  return parsed.data;
}

function toQueryString<T extends object>(params: T): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export interface AdminListQuery {
  cursor?: string;
  limit?: number;
}

export const adminApiClient = {
  login: (username: string, password: string) =>
    adminRequest<AdminSessionResponse>("/api/v1/admin/login", adminSessionResponseSchema, {
      method: "POST",
      body: adminLoginRequestSchema.parse({ username, password }),
    }),

  getMetrics: () => adminRequest<AdminMetrics>("/api/v1/admin/metrics", adminMetricsSchema),

  listUsers: (query: AdminListQuery & { search?: string } = {}) =>
    adminRequest(
      `/api/v1/admin/users${toQueryString(query)}`,
      paginatedResponseSchema(adminUserSummarySchema),
    ),

  getUserDetail: (userId: string) =>
    adminRequest<AdminUserDetail>(`/api/v1/admin/users/${userId}`, adminUserDetailSchema),

  setUserStatus: (userId: string, status: "ACTIVE" | "SUSPENDED") =>
    adminRequest(`/api/v1/admin/users/${userId}/status`, adminUserSummarySchema, {
      method: "POST",
      body: adminSetUserStatusRequestSchema.parse({ status }),
    }),

  listListings: (query: AdminListQuery = {}) =>
    adminRequest(
      `/api/v1/admin/listings${toQueryString(query)}`,
      paginatedResponseSchema(listingResponseSchema),
    ),

  suspendListing: (listingId: string) =>
    adminRequest(`/api/v1/admin/listings/${listingId}/suspend`, listingResponseSchema, {
      method: "POST",
    }),

  deleteListing: (listingId: string) =>
    adminRequest(`/api/v1/admin/listings/${listingId}/delete`, z.object({ deleted: z.boolean() }), {
      method: "POST",
    }),

  listDisputes: (query: AdminListQuery & { status?: string } = {}) =>
    adminRequest(
      `/api/v1/admin/disputes${toQueryString(query)}`,
      paginatedResponseSchema(disputeResponseSchema),
    ),

  listFlaggedMessages: (query: AdminListQuery = {}) =>
    adminRequest(
      `/api/v1/admin/flagged-messages${toQueryString(query)}`,
      paginatedResponseSchema(adminFlaggedMessageSchema),
    ),

  listAuditEvents: (query: AdminListQuery & { action?: string } = {}) =>
    adminRequest(
      `/api/v1/admin/audit-events${toQueryString(query)}`,
      paginatedResponseSchema(adminAuditEventSchema),
    ),

  getMaintenanceMode: () =>
    adminRequest<MaintenanceModeResponse>(
      "/api/v1/admin/maintenance-mode",
      maintenanceModeResponseSchema,
    ),

  setMaintenanceMode: (body: SetMaintenanceModeRequest) =>
    adminRequest<MaintenanceModeResponse>(
      "/api/v1/admin/maintenance-mode",
      maintenanceModeResponseSchema,
      { method: "POST", body: setMaintenanceModeRequestSchema.parse(body) },
    ),

  listTickets: (query: AdminListQuery & { status?: string } = {}) =>
    adminRequest(
      `/api/v1/admin/tickets${toQueryString(query)}`,
      paginatedResponseSchema(ticketResponseSchema),
    ),

  getTicket: (ticketId: string) =>
    adminRequest<TicketDetailResponse>(
      `/api/v1/admin/tickets/${ticketId}`,
      ticketDetailResponseSchema,
    ),

  addTicketMessage: (ticketId: string, body: CreateTicketMessageRequest) =>
    adminRequest<TicketDetailResponse>(
      `/api/v1/admin/tickets/${ticketId}/messages`,
      ticketDetailResponseSchema,
      { method: "POST", body: createTicketMessageRequestSchema.parse(body) },
    ),

  setTicketStatus: (ticketId: string, body: AdminSetTicketStatusRequest) =>
    adminRequest(`/api/v1/admin/tickets/${ticketId}/status`, ticketResponseSchema, {
      method: "POST",
      body: adminSetTicketStatusRequestSchema.parse(body),
    }),

  setTicketPriority: (ticketId: string, body: AdminSetTicketPriorityRequest) =>
    adminRequest(`/api/v1/admin/tickets/${ticketId}/priority`, ticketResponseSchema, {
      method: "POST",
      body: adminSetTicketPriorityRequestSchema.parse(body),
    }),

  listCoupons: (query: AdminListQuery = {}) =>
    adminRequest(
      `/api/v1/admin/coupons${toQueryString(query)}`,
      paginatedResponseSchema(couponResponseSchema),
    ),

  createCoupon: (body: CreateCouponRequest) =>
    adminRequest(`/api/v1/admin/coupons`, couponResponseSchema, {
      method: "POST",
      body: createCouponRequestSchema.parse(body),
    }),

  deactivateCoupon: (couponId: string) =>
    adminRequest(`/api/v1/admin/coupons/${couponId}/deactivate`, couponResponseSchema, {
      method: "POST",
    }),

  listAccountTransfers: (query: AdminListQuery = {}) =>
    adminRequest(
      `/api/v1/admin/account-transfers${toQueryString(query)}`,
      paginatedResponseSchema(accountTransferQueueItemSchema),
    ),

  advanceAccountTransferToVerifying: (orderId: string) =>
    adminRequest(`/api/v1/admin/account-transfers/${orderId}/verify`, z.object({ ok: z.boolean() }), {
      method: "POST",
      body: { stage: "PLATFORM_VERIFYING" },
    }),

  submitAccountTransferCredentials: (
    orderId: string,
    body: SubmitAccountTransferCredentialsRequest,
  ) =>
    adminRequest(
      `/api/v1/admin/account-transfers/${orderId}/credentials`,
      z.object({ ok: z.boolean() }),
      { method: "POST", body: submitAccountTransferCredentialsRequestSchema.parse(body) },
    ),

  getAccountTransferSellerHandoff: (orderId: string) =>
    adminRequest(
      `/api/v1/admin/account-transfers/${orderId}/seller-handoff`,
      accountTransferSellerHandoffResponseSchema,
    ),

  listDomainTransfers: (query: AdminListQuery = {}) =>
    adminRequest(
      `/api/v1/admin/domain-transfers${toQueryString(query)}`,
      paginatedResponseSchema(domainTransferQueueItemSchema),
    ),

  getDomainTransferCredential: (orderId: string) =>
    adminRequest(
      `/api/v1/admin/domain-transfers/${orderId}/credential`,
      domainTransferCredentialResponseSchema,
    ),

  listPosts: (query: AdminListQuery = {}) =>
    adminRequest(
      `/api/v1/admin/posts${toQueryString(query)}`,
      paginatedResponseSchema(postSummaryResponseSchema),
    ),

  getPost: (postId: string) => adminRequest(`/api/v1/admin/posts/${postId}`, postResponseSchema),

  createPost: (body: CreatePostRequest) =>
    adminRequest(`/api/v1/admin/posts`, postResponseSchema, {
      method: "POST",
      body: createPostRequestSchema.parse(body),
    }),

  updatePost: (postId: string, body: UpdatePostRequest) =>
    adminRequest(`/api/v1/admin/posts/${postId}`, postResponseSchema, {
      method: "PATCH",
      body: updatePostRequestSchema.parse(body),
    }),

  publishPost: (postId: string) =>
    adminRequest(`/api/v1/admin/posts/${postId}/publish`, postResponseSchema, { method: "POST" }),

  unpublishPost: (postId: string) =>
    adminRequest(`/api/v1/admin/posts/${postId}/unpublish`, postResponseSchema, {
      method: "POST",
    }),

  deletePost: (postId: string) =>
    adminRequest(`/api/v1/admin/posts/${postId}/delete`, z.object({ deleted: z.boolean() }), {
      method: "POST",
    }),
};
